import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useLazyQuery } from "@apollo/client";
import { ORDERS_QUERY } from "../queries";
import { Variables } from "../types";
import EmptyState from "@/components/EmptyState";
import Table, {
  TableControls,
  TableFooter,
  TableHeading,
  buildActionColumn,
  buildCopyTextColumn
} from "@/components/Table";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import { toFormat } from "@/lib/dateHelpers";
import useCapabilities, { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import { Path } from "@/router";
import usePagination from "@/hooks/usePagination";
import { Controller, useForm } from "react-hook-form";
import SearchInput from "@/components/SearchInput";
import { intersectionBy, reject, isEmpty } from "lodash";
import { ChangeDueDateModal } from "./ChangeDueDateModal";
import { TerminateModal } from "./TerminateModal";
import { SuspendModal } from "./SuspendModal";
import { RowSelectionState } from "@tanstack/react-table";
import Button from "@/components/Button";
import pluralize from "@/lib/pluralize";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import MultiSelect from "@/components/MultiSelect";
import DateSelect from "@/components/DateSelect";
import { NumberFormatBase } from "react-number-format";
import TextField from "@/components/TextField";
import { isNull, get } from "lodash";
import FacilityAutocomplete from "../../../_components/FacilityAutocomplete";
import { FormProvider } from "react-hook-form";
import SectionAutocomplete from "../../../_components/SectionAutocomplete";
import { currencyFormatter } from "@/lib/currencyHelpers";
import { useParams, useSearchWithParams } from "@/hooks/useSearchParams";
import { OrderRow, OrderNode } from "@/pages/billing/types";
import { buildColumns, renderStatus } from "@/pages/billing/_utils";
import { GET_CLASSIFICATIONS } from "../../../queries";
import { ClassificationType, StatsType } from "../../types";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { useMutation } from "@apollo/client";
import { TERMINATE_PROGRAMS_MUTATION, SUSPEND_PROGRAMS_MUTATION, CHANGE_DUE_DATES_MUTATION } from "../queries";
import { showSnackbar } from "@/lib/snackbarUtils";
import { MaterialNames } from "@/components/material_symbol_names";
import MobileSortDrawer from "./MobileSortDrawer";
import { ColumnSort } from "@tanstack/react-table";
import useAmplitude from "@/hooks/useAmplitude";
import { BILLING_ANALYTICS_EVENTS, getBillingAnalyticsData } from "@/pages/billing/_utils/amplitude";
import MetricGrid from "@/components/MetricGrid";

export type FormValues = {
  maxBalance: string;
  memberClassifications: string[];
  paymentStatuses: [];
  sectionIds: string[];
  facilityIds: string[];
  dueDateRange: { startDate: Date | null; endDate: Date | null };
};

const mapCommerceOrders = (data: OrderNode[]) =>
  (data || []).map(({ updatedAt, dueDate, balance, lineItems, ...order }) => ({
    ...order,
    pgaId: order.person.pgaId,
    classification: order.person.classification || "",
    section: order.person.primarySectionAffiliation?.section.name || "",
    updatedAt: formatDate(updatedAt),
    dueDate: formatDate(dueDate),
    balance: centsToDollarsFormatted(balance) || undefined,
    products: lineItems.length
  }));

const formatDate = (date?: string): string | undefined => (date && toFormat(date)) || undefined;

export const OrdersTable = ({ defaultVariables }: { defaultVariables: Variables }) => {
  useRequireCapability({ domain: DOMAIN.COMMERCE, permission: PERMISSION.viewOrders });
  const { trackEvent } = useAmplitude();
  const { hasPermission } = useCapabilities();
  const availableForSuspensions = Object.keys(defaultVariables)[0] === "availableForSuspensions";

  let { params, addSearchParam, deleteSearchParams, searchParamsUrl } = useParams();
  params.balanceRange = params.maxBalance && { to: Number(params.maxBalance) };
  params.dueDateRange = params.dueDateRange && { from: null, to: params.dueDateRange || null };

  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({ paramName: "search" });
  const [openChangeDueDateModal, setOpenChangeDueDateModal] = useState(false);
  const [openTerminateModal, setOpenTerminateModal] = useState(false);
  const [openSuspendModal, setOpenSuspendModal] = useState(false);
  const [terminateAll, setTerminateAll] = useState(false);
  const [suspendAll, setSuspendAll] = useState(false);

  const [selectedOrders, setSelectedOrders] = useState<any>();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const rowSelected = Object.keys(rowSelection).length;
  const [selectedData, setSelectedData] = useState<OrderRow[]>([]);
  const [isSelectedOnly, setIsSelectedOnly] = useState(false);
  const [isMobileSortOpen, setIsMobileSortOpen] = useState(false);
  const [orderBy, setOrderBy] = useState<ColumnSort[]>([]);

  const [selectedValues, setSelectedValues] = useState<string[]>(params.paymentStatuses || []);
  const [selectedClassificationValues, setSelectedClassificationValues] = useState<string[]>(
    params.memberClassifications || []
  );

  const [variables, setVariables] = useState<Variables>({ ...defaultVariables, ...params });
  const { data, loading, refetch } = useQuery(ORDERS_QUERY, { variables: { ...variables, ...paginationVariables } });
  const [selectedOrdersQuery, { data: allSelectedData, loading: allSelectedLoading }] = useLazyQuery(ORDERS_QUERY);
  const { data: classificationData } = useQuery(GET_CLASSIFICATIONS);
  const [terminateProgramsMutation, { loading: terminateLoading }] = useMutation(TERMINATE_PROGRAMS_MUTATION);
  const [suspendProgramsProgramsMutation, { loading: suspendLoading }] = useMutation(SUSPEND_PROGRAMS_MUTATION);
  const [changeDueDateMutation, { loading: changeDueDateLoading }] = useMutation(CHANGE_DUE_DATES_MUTATION);

  const { Menu, setMenu } = useTableRowMenu();
  const { isCompactLayout } = useIsCompactLayout();

  const form = useForm<FormValues>();
  const { control, reset, watch, getValues, setValue } = form;

  const pattern = "/billing/orders";
  const returnPath = availableForSuspensions
    ? `/billing/dues-management/suspensions-and-terminations/suspensions&${searchParamsUrl}`
    : `/billing/dues-management/suspensions-and-terminations/terminations&${searchParamsUrl}`;

  const orderData: OrderRow[] = mapCommerceOrders(data?.duesOrders?.nodes || []);

  const allSelectedDataArray: OrderRow[] = mapCommerceOrders(allSelectedData?.duesOrders?.nodes || []);

  const getIsSomeRowsSelected = () => {
    return intersectionBy(selectedData, orderData, "id").length > 0 && selectedData.length !== orderData.length;
  };

  const [stats, setStats] = useState<StatsType>({
    totalCount: null,
    failedPayments: null
  });

  useEffect(() => {
    if (!loading && data) {
      if (stats.totalCount === null || stats.failedPayments == null) {
        setStats({
          totalCount: data.totalDuesOrders?.totalCount,
          failedPayments: data.totalAvailableDuesOrdersWithFailedPayments?.totalCount
        });
      }
      setValue("sectionIds", params.sectionIds);
      setValue("facilityIds", params.facilityIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data]);
  const sectionIds = watch("sectionIds");
  const facilityIds = watch("facilityIds");

  useEffect(() => {
    setVariables((prevValue) => ({
      ...prevValue,
      ...(sectionIds ? { sectionIds: sectionIds } : {}),
      ...(facilityIds ? { facilityIds: facilityIds } : {})
    }));
  }, [sectionIds, facilityIds]);
  const totalOrders = data?.totalDuesOrders?.totalCount;

  useEffect(() => {
    setRowSelection({});
  }, [totalOrders]);

  useEffect(() => {
    setVariables((prevValue) => ({
      ...prevValue,
      search: debouncedSearch
    }));
  }, [debouncedSearch]);

  const skipDebouncedSearchPaginationReset = useRef(true);
  useEffect(() => {
    if (skipDebouncedSearchPaginationReset.current) {
      skipDebouncedSearchPaginationReset.current = false;
      return;
    }
    resetPagination();
  }, [debouncedSearch, resetPagination]);

  const filters = {
    maxBalance: !!getValues("maxBalance"),
    section: !!sectionIds,
    paymentStatuses: selectedValues.length > 0,
    memberClassifications: selectedClassificationValues.length > 0,
    dueDateRange: !!variables.dueDateRange?.to,
    facility: !!facilityIds
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const hasActiveFilters = activeFilterCount > 0 || !!(searchValue || "").trim();
  const detailsBody = availableForSuspensions
    ? "List of Dues orders that have not been fully paid and are overdue. Multi-select people to suspend a subset, or press “Suspend All” to start the suspension process for everyone."
    : "List of Dues orders that have not been fully paid and are overdue by more than 30 days. Multi-select people to terminate a subset, or press “Terminate All” to start the termination process for everyone.";

  const bulkActions =
    rowSelected > 0 && hasPermission(PERMISSION.manageOrders)
      ? [
          ...(availableForSuspensions
            ? [
                {
                  label: "Suspend Selected",
                  variant: "outlined" as const,
                  onClick: () => {
                    setSelectedOrders(rowSelection);
                    setSuspendAll(false);
                    setOpenSuspendModal(true);
                  }
                }
              ]
            : [
                {
                  label: "Terminate Selected",
                  variant: "outlined" as const,
                  onClick: () => {
                    setSelectedOrders(rowSelection);
                    setTerminateAll(false);
                    setOpenTerminateModal(true);
                  }
                }
              ]),
          {
            label: "Change Due Date",
            variant: "outlined" as const,
            onClick: () => {
              setSelectedOrders(rowSelection);
              setOpenChangeDueDateModal(true);
            }
          }
        ]
      : [];

  const classificationNodes = get(classificationData, "classifications.nodes");
  const classificationDataNodes =
    classificationNodes?.map((node: ClassificationType) => ({
      value: node.name,
      label: node.name
    })) || [];

  const onClearAll = () => {
    setVariables(defaultVariables);
    setSelectedValues([]);
    setSelectedClassificationValues([]);
    reset({
      dueDateRange: { startDate: null, endDate: null }
    });
    deleteSearchParams([
      "dueDateRange",
      "maxBalance",
      "memberClassifications[]",
      "paymentStatuses[]",
      "sectionIds[]",
      "section",
      "facilityIds[]",
      "facility"
    ]);
  };

  const [errorContent, setErrorContent] = useState("");
  const activeFilterTotalOrders = data?.duesOrders?.totalCount;
  async function terminatePrograms(inputVariables: any, close: () => void) {
    const updatedVariables = {
      ...inputVariables,
      ...(terminateAll && {
        ...(stats.totalCount !== activeFilterTotalOrders
          ? {
              ordersSearch: variables
            }
          : { terminateAll: true })
      })
    };

    const { data } = await terminateProgramsMutation({
      variables: { input: updatedVariables }
    });

    if (data.duesBulkTerminatePrograms.success) {
      const successfulPersonIds = data.duesBulkTerminatePrograms.successfulPersonIds.length;
      showSnackbar(`${successfulPersonIds} people have been terminated`);

      setStats({
        totalCount: null,
        failedPayments: null
      });
      if (updatedVariables.ordersSearch) {
        onClearAll();
        deleteSearchParams("search");
        clearSearch();
      } else {
        refetch();
      }
      if (data.duesBulkTerminatePrograms.failedPersonIds.length > 0) {
        setErrorContent(data.duesBulkTerminatePrograms.message);
        refetch();
      } else {
        close();
      }
      trackEvent(
        BILLING_ANALYTICS_EVENTS.programsTerminated,
        getBillingAnalyticsData({
          source: "suspensions_and_terminations",
          personCount: successfulPersonIds,
          action: terminateAll ? "terminate_all_or_filtered" : "terminate_selected"
        })
      );
    } else {
      setErrorContent(data.duesBulkTerminatePrograms.message);
    }
  }

  async function suspendPrograms(inputVariables: any, close: () => void) {
    const updatedVariables = {
      ...inputVariables,
      ...(suspendAll && {
        ...(stats.totalCount !== activeFilterTotalOrders
          ? {
              ordersSearch: variables
            }
          : { suspendAll: true })
      })
    };

    const { data } = await suspendProgramsProgramsMutation({
      variables: { input: updatedVariables }
    });

    if (data.duesBulkSuspendPrograms.success) {
      const successfulPersonIds = data.duesBulkSuspendPrograms.successfulPersonIds.length;
      showSnackbar(`${successfulPersonIds} people have been suspended`);

      setStats({
        totalCount: null,
        failedPayments: null
      });
      if (updatedVariables.ordersSearch) {
        onClearAll();
        deleteSearchParams("search");
        clearSearch();
      }
      refetch();
      if (data.duesBulkSuspendPrograms.failedPersonIds.length > 0) {
        setErrorContent(data.duesBulkSuspendPrograms.message);
        refetch();
      } else {
        close();
      }
      trackEvent(
        BILLING_ANALYTICS_EVENTS.programsSuspended,
        getBillingAnalyticsData({
          source: "suspensions_and_terminations",
          personCount: successfulPersonIds,
          action: suspendAll ? "suspend_all_or_filtered" : "suspend_selected"
        })
      );
    } else {
      setErrorContent(data.duesBulkSuspendPrograms.message);
    }
  }

  async function changeDueDate(variables: Variables, close: () => void, sendNotification: boolean) {
    const { data } = await changeDueDateMutation({
      variables: { input: variables }
    });

    if (data.commerceBulkChangeOrderDueDate.success) {
      const successfulOrders = data.commerceBulkChangeOrderDueDate.successfulOrders.length;
      showSnackbar(`${successfulOrders} ${pluralize(successfulOrders, "order")} due dates have been extended`);
      if (sendNotification) {
        showSnackbar("Email Notification Sent");
      }

      setStats({
        totalCount: null,
        failedPayments: null
      });
      refetch();
      if (data.commerceBulkChangeOrderDueDate.failedOrders.length > 0) {
        setErrorContent(data.commerceBulkChangeOrderDueDate.message);
        refetch();
      } else {
        close();
      }
      trackEvent(
        BILLING_ANALYTICS_EVENTS.bulkOrderDueDatesChanged,
        getBillingAnalyticsData({
          source: "suspensions_and_terminations",
          orderCount: successfulOrders,
          action: sendNotification ? "bulk_change_due_date_with_notification" : "bulk_change_due_date"
        })
      );
    } else {
      setErrorContent(data.commerceBulkChangeOrderDueDate.message);
    }
  }
  const ActionButton = useMemo(() => {
    return (
      <Button
        variant="filled"
        disabled={activeFilterTotalOrders === 0}
        onClick={() => {
          if (availableForSuspensions) {
            setSuspendAll(true);
            setOpenSuspendModal(true);
          } else {
            setTerminateAll(true);
            setOpenTerminateModal(true);
          }
          setSelectedOrders([]);
        }}
      >
        {availableForSuspensions ? "Suspend" : "Terminate"}{" "}
        {data?.totalDuesOrders?.totalCount === activeFilterTotalOrders || loading
          ? "All"
          : activeFilterTotalOrders + " People"}
      </Button>
    );
  }, [activeFilterTotalOrders, availableForSuspensions, loading, data]);

  return (
    <>
      {stats.totalCount === 0 ? (
        <EmptyState
          title={`No-one Eligible for ${availableForSuspensions ? "Suspension" : "Termination"} for Overdue Dues`}
          iconName="person"
        />
      ) : (
        <>
          <MetricGrid
            metrics={[
              {
                label: `Total Eligible for ${availableForSuspensions ? "Suspension" : "Termination"}`,
                value: `${!isNull(stats.totalCount) && stats.totalCount}`,
                loading: isNull(stats.totalCount),
                iconName: "person" as MaterialNames,
                ...(stats.totalCount !== null && stats.totalCount > 0 && { variant: "warn" as const })
              },
              {
                label: `Orders in Active Filter(s)`,
                value: `${activeFilterTotalOrders}`,
                loading: loading,
                iconName: "receipt_long" as MaterialNames
              },
              {
                label: "Total Orders with Failed Payment",
                value: `${!isNull(stats.failedPayments) && stats.failedPayments}`,
                variant:
                  stats.failedPayments !== null && stats.failedPayments !== undefined && stats.failedPayments > 0
                    ? "error"
                    : undefined,
                loading: isNull(stats.failedPayments),
                iconName: "receipt_long" as MaterialNames
              }
            ]}
            layout="expanded"
          />
          <Menu />
          <TableHeading
            heading="Overdue Orders"
            body={detailsBody}
            actions={
              rowSelected === 0
                ? [
                    {
                      label: availableForSuspensions ? "Suspend All" : "Terminate All",
                      onClick: () => {
                        if (availableForSuspensions) {
                          setSuspendAll(true);
                          setOpenSuspendModal(true);
                        } else {
                          setTerminateAll(true);
                          setOpenTerminateModal(true);
                        }
                        setSelectedOrders([]);
                      },
                      variant: "filled",
                      disabled: activeFilterTotalOrders === 0,
                      render: () => ActionButton
                    }
                  ]
                : []
            }
          />
          <div className="w-full">
            <Table
              data={(isSelectedOnly ? allSelectedDataArray : orderData) || []}
              loading={loading || allSelectedLoading}
              getRowLink={(row) => ({
                to: `${pattern}/${row.original.id}?return_to=${returnPath}` as Path
              })}
              rowSelection={rowSelection}
              onRowSelectionChange={setRowSelection}
              setSelectedData={setSelectedData}
              hasActiveFilters={hasActiveFilters}
              renderNoDataEmptyState={() => (
                <EmptyState
                  title={`No-one Eligible for ${availableForSuspensions ? "Suspension" : "Termination"} for Overdue Dues`}
                  iconName="person"
                />
              )}
              renderFilteredEmptyState={() => (
                <EmptyState title="No Results Found" caption="Try a different search or filter" iconName="search" />
              )}
              renderControls={(table) => (
                <FormProvider {...form}>
                  <TableControls
                    pagination={createHeaderPagination(data?.duesOrders, {
                      totalCount: activeFilterTotalOrders || 0,
                      pageCount: orderData.length,
                      loading: loading || allSelectedLoading
                    })}
                    onSortClick={() => setIsMobileSortOpen(true)}
                    useExpandedFilters
                    expandedFiltersState={filters}
                    activeFilters={activeFilterCount}
                    onClearAllFilters={() => {
                      onClearAll();
                      clearSearch();
                      resetPagination();
                    }}
                    bulkActions={bulkActions}
                    rowSelectionCount={rowSelected}
                    isSelectedOnly={isSelectedOnly}
                    onShowSelectedOnly={() => {
                      setIsSelectedOnly(!isSelectedOnly);
                      selectedOrdersQuery({ variables: { ids: Object.keys(rowSelection) } });
                    }}
                    totalRowCount={rowSelected}
                    onDeselectAll={() => {
                      setRowSelection({});
                      setIsSelectedOnly(false);
                      resetPagination();
                    }}
                    onToggleAllRows={(checked) => table.toggleAllRowsSelected(checked)}
                    allRowsSelected={table.getIsAllRowsSelected()}
                    someRowsSelected={table.getIsSomeRowsSelected()}
                  >
                    <SearchInput
                      placeholder="Search Orders"
                      query={searchValue}
                      onClear={() => {
                        clearSearch();
                        resetPagination();
                      }}
                      onChange={(value) => {
                        setSearchValue(value);
                        resetPagination();
                      }}
                    />
                    <div data-name="maxBalance">
                      <Controller
                        name="maxBalance"
                        control={control}
                        defaultValue={params.maxBalance || ""}
                        render={({ field }) => {
                          return (
                            <NumberFormatBase
                              label="Max Balance"
                              placeholder="Enter Amount"
                              leadingIcon="attach_money"
                              customInput={TextField}
                              format={currencyFormatter}
                              value={field.value ?? ""}
                              name={field.name}
                              onValueChange={(values) => {
                                field.onChange(values.floatValue);
                                if (values.floatValue) {
                                  setVariables((prevValue) => ({
                                    ...prevValue,
                                    balanceRange: { to: values.floatValue }
                                  }));
                                  addSearchParam("maxBalance", values.floatValue.toString());
                                } else {
                                  setVariables((prevValue) => {
                                    const { balanceRange, ...rest } = prevValue;
                                    return {
                                      ...rest
                                    };
                                  });
                                  deleteSearchParams("maxBalance");
                                }
                              }}
                              className="w-full"
                            />
                          );
                        }}
                      />
                    </div>
                    <div data-name="section">
                      <SectionAutocomplete
                        params={params}
                        addSearchParam={addSearchParam}
                        deleteSearchParams={deleteSearchParams}
                        name="section"
                      />
                    </div>
                    <div data-name="memberClassifications">
                      <Controller
                        name="memberClassifications"
                        control={control}
                        render={() => (
                          <MultiSelect
                            label="Classification"
                            selectedValues={selectedClassificationValues}
                            options={classificationDataNodes}
                            onSelect={(value, checked) => {
                              const status = checked
                                ? [...selectedClassificationValues, value]
                                : selectedClassificationValues.filter((v) => v !== value);
                              setSelectedClassificationValues(reject(status, isEmpty));
                              setVariables((prevValue) => ({
                                ...prevValue,
                                memberClassifications: reject(status, isEmpty)
                              }));
                              addSearchParam("memberClassifications[]", reject(status, isEmpty).toString());
                            }}
                            onClear={() => {
                              setSelectedClassificationValues([]);
                              setVariables((prevValue) => ({
                                ...prevValue,
                                memberClassifications: []
                              }));
                              deleteSearchParams("memberClassifications[]");
                            }}
                            className="w-full"
                          />
                        )}
                      />
                    </div>
                    <div data-name="paymentStatuses">
                      <Controller
                        name="paymentStatuses"
                        control={control}
                        render={() => (
                          <MultiSelect
                            label="Status"
                            selectedValues={selectedValues}
                            options={[
                              { value: "HAS_FAILED_PAYMENTS", label: "Has Failed Payment" },
                              { value: "HAS_REFUNDED_PAYMENTS", label: "Has Refunded Payment" }
                            ]}
                            onSelect={(value, checked) => {
                              const status = checked
                                ? [...selectedValues, value]
                                : selectedValues.filter((v) => v !== value);

                              setSelectedValues(reject(status, isEmpty));
                              setVariables((prevValue) => ({
                                ...prevValue,
                                paymentStatuses: reject(status, isEmpty)
                              }));
                              addSearchParam("paymentStatuses[]", reject(status, isEmpty).toString());
                            }}
                            onClear={() => {
                              setSelectedValues([]);
                              setVariables((prevValue) => ({
                                ...prevValue,
                                paymentStatuses: []
                              }));
                              deleteSearchParams("paymentStatuses[]");
                            }}
                            className="w-full"
                          />
                        )}
                      />
                    </div>
                    <div data-name="dueDateRange" data-filter-size="custom">
                      <Controller
                        name="dueDateRange"
                        control={control}
                        render={({ field }) => {
                          return (
                            <DateSelect
                              placeholder="Select a Date"
                              label="Due Date on or Before"
                              useModal={true}
                              value={field.value}
                              useSingleDate
                              onChange={(e) => {
                                field.onChange(e);
                                if (typeof e === "object" && e !== null) {
                                  setVariables((prevValue) => ({
                                    ...prevValue,
                                    dueDateRange: { from: null, to: e.endDate }
                                  }));
                                  addSearchParam("dueDateRange", e.endDate?.toString() || "");
                                }
                              }}
                            />
                          );
                        }}
                      />
                    </div>
                    <div data-name="facility">
                      <FacilityAutocomplete
                        sectionIds={variables?.sectionIds}
                        params={params}
                        addSearchParam={addSearchParam}
                        deleteSearchParams={deleteSearchParams}
                        name="facility"
                      />
                    </div>
                  </TableControls>
                </FormProvider>
              )}
              columns={[
                ...buildColumns({
                  isCompactLayout,
                  hasCheckbox: true,
                  selectableRows: orderData,
                  getIsSomeRowsSelected,
                  setIsSelectedOnly
                }),
                buildCopyTextColumn<OrderRow>({
                  accessorKey: "pgaId",
                  header: "PGA ID",
                  copyFieldLabel: "PGA ID",
                  getText: (row) => row.pgaId,
                  enableSorting: false
                }),
                { accessorKey: "classification", header: "Classification", enableSorting: false },
                { accessorKey: "section", header: "Section", enableSorting: false },
                {
                  accessorKey: "products",
                  header: "Products",
                  enableSorting: false
                },
                { accessorKey: "updatedAt", header: "Modified", enableSorting: false },
                { accessorKey: "dueDate", header: "Due Date", enableSorting: false },
                { accessorKey: "balance", header: "Balance", enableSorting: false },
                {
                  header: "Stage",
                  id: "stage",
                  cell: ({ row: { original } }) => renderStatus(original.status, original.paymentStatus),
                  size: 100
                },
                ...(hasPermission(PERMISSION.manageOrders)
                  ? [
                      buildActionColumn<OrderRow>({
                        getActions: (row) => [
                          {
                            text: "Change Due Date",
                            onClick: () => {
                              setSelectedOrders({ [`${row.id}`]: true });
                              setOpenChangeDueDateModal(true);
                            }
                          }
                        ],
                        actionId,
                        setMenu
                      })
                    ]
                  : [])
              ]}
              renderFooter={() => (
                <TableFooter
                  pagination={
                    !isSelectedOnly
                      ? createFooterPagination(data?.duesOrders, {
                          totalCount: activeFilterTotalOrders || 0,
                          pageCount: orderData.length,
                          loading: loading || allSelectedLoading
                        })
                      : undefined
                  }
                  totalRowCount={activeFilterTotalOrders}
                  rowSelectionCount={rowSelected}
                  isSelectedOnly={isSelectedOnly}
                  onShowSelectedOnly={() => {
                    setIsSelectedOnly(!isSelectedOnly);
                    selectedOrdersQuery({ variables: { ids: Object.keys(rowSelection) } });
                  }}
                />
              )}
            />
          </div>
        </>
      )}
      {openChangeDueDateModal && (
        <ChangeDueDateModal
          onDismiss={() => {
            setOpenChangeDueDateModal(false);
          }}
          orders={selectedOrders}
          onSubmit={(variables, close, sendNotification = false) => changeDueDate(variables, close, sendNotification)}
          loading={changeDueDateLoading}
          errorContent={errorContent}
        />
      )}
      {openTerminateModal && (
        <TerminateModal
          onDismiss={() => {
            setOpenTerminateModal(false);
          }}
          orders={selectedOrders}
          terminateAll={terminateAll}
          totalPeople={activeFilterTotalOrders || 0}
          onSubmit={terminatePrograms}
          loading={terminateLoading}
          errorContent={errorContent}
        />
      )}
      {openSuspendModal && (
        <SuspendModal
          onDismiss={() => {
            setOpenSuspendModal(false);
          }}
          orders={selectedOrders || {}}
          suspendAll={suspendAll}
          totalPeople={activeFilterTotalOrders || 0}
          onSubmit={suspendPrograms}
          loading={suspendLoading}
          errorContent={errorContent}
        />
      )}

      <MobileSortDrawer
        isOpen={isMobileSortOpen}
        onClose={() => setIsMobileSortOpen(false)}
        onSortChange={setOrderBy}
        currentSort={orderBy}
      />
    </>
  );
};

export default OrdersTable;
